import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { revisionApi, competitiveExamsApi } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Sun, Moon, BookOpen, Sparkles, Layers, Search,
  Loader2, Copy, CheckCircle2, Clock, Star, FlameIcon, Trophy,
  ChevronRight, RefreshCw, Zap,
} from 'lucide-react';

// Phase 34 Revision Hub: Formula Book · Quick Notes · Flashcards
export default function RevisionHub() {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState('jee_main');
  const [tab, setTab] = useState('formulas');

  useEffect(() => { competitiveExamsApi.getAll().then(setExams).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-border bg-card/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="rev-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-emerald-500" />Revision Hub
              </h1>
              <p className="text-xs text-muted-foreground">Formulas, 2-page notes, and smart-review flashcards — all in one place.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={logout}>Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-5">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold">Exam:</span>
          <Select value={examId} onValueChange={setExamId}>
            <SelectTrigger className="w-64" data-testid="rev-exam-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {exams.map(e => <SelectItem key={e.exam_id} value={e.exam_id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border overflow-x-auto" data-testid="rev-tabs">
          {[
            { id: 'formulas', label: 'Formula Book', icon: Layers },
            { id: 'notes', label: 'Quick Notes', icon: Sparkles },
            { id: 'flashcards', label: 'Flashcards', icon: Zap },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`rev-tab-${t.id}`}
              className={`px-4 py-2.5 text-sm font-medium flex items-center gap-2 whitespace-nowrap border-b-2 transition ${tab === t.id ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>

        {tab === 'formulas' && <FormulaBookTab examId={examId} />}
        {tab === 'notes' && <QuickNotesTab examId={examId} />}
        {tab === 'flashcards' && <FlashcardsTab examId={examId} />}
      </div>
    </div>
  );
}

// ---------- Formula Book Tab ----------
function FormulaBookTab({ examId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await revisionApi.formulaBook(examId);
      setData(r);
    } catch {
      toast.error('Failed to load Formula Book');
    }
    setLoading(false);
  }, [examId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const filtered = (data?.subjects || []).map(sub => ({
    ...sub,
    formulas: sub.formulas.filter(f =>
      !search ||
      f.formula?.toLowerCase().includes(search.toLowerCase()) ||
      f.meaning?.toLowerCase().includes(search.toLowerCase()) ||
      f.topic?.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter(s => s.formulas.length > 0);

  if (!data?.total_count) {
    return (
      <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl" data-testid="formula-book-empty">
        <Layers className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
        <p className="font-semibold">No formulas yet</p>
        <p className="text-sm text-muted-foreground mb-4">Open a study material topic first — its formulas will appear here automatically.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="formula-book">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-border bg-card">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            type="text" placeholder="Search formula, meaning, or topic…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="formula-search"
            className="flex-1 bg-transparent outline-none text-sm"
          />
        </div>
        <Badge variant="outline" className="text-xs" data-testid="formula-total">
          {data.total_count} formulas indexed
        </Badge>
      </div>

      {filtered.map((sub, i) => (
        <div key={sub.subject} className="p-5 rounded-2xl border border-border bg-card" data-testid={`formula-subject-${i}`}>
          <h3 className="font-bold mb-3 flex items-center gap-2">
            <Layers className="w-4 h-4 text-emerald-500" />{sub.subject}
            <Badge variant="outline" className="text-[10px]">{sub.formulas.length}</Badge>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sub.formulas.map((f, fi) => (
              <FormulaCard key={fi} formula={f} index={fi} subjectIdx={i} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function FormulaCard({ formula, index, subjectIdx }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(formula.formula);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <div className="p-3 rounded-xl border border-border bg-background/50 hover:border-emerald-400 transition" data-testid={`formula-card-${subjectIdx}-${index}`}>
      <div className="flex items-start gap-2">
        <code className="flex-1 font-mono text-sm font-bold text-emerald-700 dark:text-emerald-400">{formula.formula}</code>
        <button onClick={copy} data-testid={`formula-copy-${subjectIdx}-${index}`} className="text-muted-foreground hover:text-foreground">
          {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
      {formula.meaning && <p className="text-xs text-muted-foreground mt-1">{formula.meaning}</p>}
      {formula.when_to_use && <p className="text-[10px] text-muted-foreground/70 mt-1"><span className="font-semibold">Use when:</span> {formula.when_to_use}</p>}
      {formula.topic && <Badge variant="outline" className="text-[10px] mt-2">{formula.topic}</Badge>}
    </div>
  );
}

// ---------- Quick Notes Tab ----------
function QuickNotesTab({ examId }) {
  const [topics, setTopics] = useState([]);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    revisionApi.notesIndex(examId).then(r => setTopics(r.topics || [])).catch(() => {});
    setSelected(null); setNote(null);
  }, [examId]);

  const loadNote = async (topic) => {
    setSelected(topic);
    setNote(null);
    setLoading(true);
    try {
      const r = await revisionApi.getNote(examId, topic.topic, topic.subject);
      setNote(r);
    } catch (e) {
      toast.error(e.message || 'Failed to load note');
      setSelected(null);
    }
    setLoading(false);
  };

  if (!topics.length) {
    return (
      <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl" data-testid="notes-empty">
        <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
        <p className="font-semibold">No notes available</p>
        <p className="text-sm text-muted-foreground">Open study material topics first — quick-revision notes auto-generate from them.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5" data-testid="quick-notes">
      <div className="space-y-1 lg:max-h-[70vh] lg:overflow-y-auto" data-testid="notes-topic-list">
        {topics.map((t, i) => (
          <button
            key={t.topic_id || i}
            onClick={() => loadNote(t)}
            data-testid={`notes-topic-${i}`}
            className={`w-full text-left px-3 py-2 rounded-lg text-xs transition ${selected?.topic === t.topic ? 'bg-emerald-500 text-white' : 'hover:bg-muted'}`}
          >
            <p className="font-semibold truncate">{t.topic}</p>
            <p className="text-[10px] opacity-70">{t.subject}</p>
          </button>
        ))}
      </div>
      <div>
        {!selected ? (
          <div className="p-12 rounded-2xl border-2 border-dashed border-border text-center text-sm text-muted-foreground" data-testid="notes-empty-state">
            Pick a topic from the left to generate a quick-revision summary.
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-16" data-testid="notes-loading">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-3" />
              <p className="text-sm font-semibold">Compressing into 2-page summary…</p>
              <p className="text-xs text-muted-foreground">Usually takes 15-30 seconds</p>
            </div>
          </div>
        ) : note ? (
          <div className="space-y-4" data-testid="notes-view">
            <div className="p-5 rounded-2xl border border-emerald-200 dark:border-emerald-900 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20">
              <Badge className="bg-emerald-500 text-white mb-2">{note.subject}</Badge>
              <h2 className="text-xl font-bold" data-testid="note-headline">{note.headline}</h2>
              {note.quick_recap && <p className="text-sm text-muted-foreground mt-2">{note.quick_recap}</p>}
            </div>

            {note.key_points?.length > 0 && (
              <Section title="Key Points" icon={Star} color="indigo" items={note.key_points} testId="note-key-points" />
            )}
            {note.must_remember?.length > 0 && (
              <Section title="Must Remember" icon={FlameIcon} color="orange" items={note.must_remember} testId="note-must-remember" />
            )}
            {note.common_traps?.length > 0 && (
              <Section title="Common Traps" icon={Clock} color="rose" items={note.common_traps} testId="note-common-traps" />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, color, items, testId }) {
  const cls = {
    indigo: 'border-indigo-200 dark:border-indigo-900 bg-indigo-50/50 dark:bg-indigo-950/10 text-indigo-500',
    orange: 'border-orange-200 dark:border-orange-900 bg-orange-50/50 dark:bg-orange-950/10 text-orange-500',
    rose:   'border-rose-200 dark:border-rose-900 bg-rose-50/50 dark:bg-rose-950/10 text-rose-500',
  }[color];
  return (
    <div className={`p-4 rounded-2xl border ${cls}`} data-testid={testId}>
      <h3 className="font-bold mb-2 flex items-center gap-2 text-foreground"><Icon className="w-4 h-4" />{title}</h3>
      <ul className="space-y-1.5 text-sm text-foreground/90">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2"><span className="opacity-60">•</span><span>{it}</span></li>
        ))}
      </ul>
    </div>
  );
}

// ---------- Flashcards Tab (SM-2) ----------
function FlashcardsTab({ examId }) {
  const [stats, setStats] = useState(null);
  const [due, setDue] = useState([]);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [reviewing, setReviewing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        revisionApi.flashcardStats(examId),
        revisionApi.dueFlashcards(examId, 30),
      ]);
      setStats(s);
      setDue(d.cards || []);
      setIdx(0); setFlipped(false);
    } catch {
      toast.error('Failed to load flashcards');
    }
    setLoading(false);
  }, [examId]);

  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    setGenerating(true);
    try {
      const r = await revisionApi.generateFlashcards(examId);
      toast.success(`${r.added} new flashcards generated · ${r.total_cards} total`);
      await load();
    } catch {
      toast.error('Failed to generate');
    }
    setGenerating(false);
  };

  const rate = async (quality) => {
    const card = due[idx];
    if (!card) return;
    setReviewing(true);
    try {
      await revisionApi.reviewFlashcard(card.card_id, quality);
      setFlipped(false);
      if (idx < due.length - 1) setIdx(idx + 1);
      else await load();
    } catch {
      toast.error('Review failed');
    }
    setReviewing(false);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const card = due[idx];

  return (
    <div className="space-y-5" data-testid="flashcards">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="flashcards-stats">
        <StatTile icon={Layers} color="indigo" label="Total cards" value={stats?.total || 0} testId="fc-stat-total" />
        <StatTile icon={Clock} color="amber" label="Due today" value={stats?.due_today || 0} testId="fc-stat-due" />
        <StatTile icon={FlameIcon} color="rose" label="Overdue" value={stats?.overdue || 0} testId="fc-stat-overdue" />
        <StatTile icon={Trophy} color="emerald" label="Mastered" value={stats?.mastered || 0} testId="fc-stat-mastered" />
      </div>

      {!stats?.total ? (
        <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl" data-testid="flashcards-empty">
          <Zap className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="font-semibold">No flashcards yet</p>
          <p className="text-sm text-muted-foreground mb-4">
            We'll auto-generate cards from your study material's formulas and common confusions.
          </p>
          <Button onClick={seed} disabled={generating} data-testid="flashcards-generate">
            {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Generating…</> : <><Sparkles className="w-4 h-4 mr-2" />Generate Flashcards</>}
          </Button>
        </div>
      ) : due.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-emerald-300 dark:border-emerald-900 rounded-2xl bg-emerald-50/30 dark:bg-emerald-950/10" data-testid="flashcards-all-done">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <p className="font-semibold text-emerald-600 dark:text-emerald-400">All caught up!</p>
          <p className="text-sm text-muted-foreground mb-3">No cards due. SM-2 scheduled your next batch.</p>
          <Button variant="outline" size="sm" onClick={seed} data-testid="flashcards-regenerate">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Check for new cards
          </Button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="flashcards-player">
          <div className="flex items-center justify-between text-xs">
            <Badge variant="outline">Card {idx + 1} / {due.length}</Badge>
            <span className="text-muted-foreground">{card.topic} · {card.subject}</span>
          </div>

          <button
            onClick={() => setFlipped(f => !f)}
            data-testid="flashcard-flip"
            className="w-full min-h-[240px] p-8 rounded-3xl border-2 border-emerald-300 dark:border-emerald-900 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 text-center transition hover:shadow-lg"
          >
            <p className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-3 font-semibold">
              {flipped ? 'Answer' : 'Question'} — tap to {flipped ? 'hide' : 'reveal'}
            </p>
            <p className="text-lg font-semibold text-foreground" data-testid={flipped ? 'flashcard-back' : 'flashcard-front'}>
              {flipped ? card.back : card.front}
            </p>
            {!flipped && <p className="text-xs text-muted-foreground mt-4">Tap to see the answer</p>}
          </button>

          {flipped && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="flashcard-rating">
              <Button variant="outline" disabled={reviewing} onClick={() => rate(1)} className="border-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30" data-testid="rate-again">
                <span className="text-xs text-left"><span className="block font-bold text-rose-600 dark:text-rose-400">Again</span><span className="text-muted-foreground">1 day</span></span>
              </Button>
              <Button variant="outline" disabled={reviewing} onClick={() => rate(3)} className="border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30" data-testid="rate-hard">
                <span className="text-xs text-left"><span className="block font-bold text-amber-600 dark:text-amber-400">Hard</span><span className="text-muted-foreground">~3 days</span></span>
              </Button>
              <Button variant="outline" disabled={reviewing} onClick={() => rate(4)} className="border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30" data-testid="rate-good">
                <span className="text-xs text-left"><span className="block font-bold text-emerald-600 dark:text-emerald-400">Good</span><span className="text-muted-foreground">~1 week</span></span>
              </Button>
              <Button variant="outline" disabled={reviewing} onClick={() => rate(5)} className="border-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/30" data-testid="rate-easy">
                <span className="text-xs text-left"><span className="block font-bold text-indigo-600 dark:text-indigo-400">Easy</span><span className="text-muted-foreground">2+ weeks</span></span>
              </Button>
            </div>
          )}
        </div>
      )}

      {stats?.by_subject?.length > 0 && (
        <div className="p-5 rounded-2xl border border-border bg-card" data-testid="flashcards-by-subject">
          <h3 className="font-bold mb-3 flex items-center gap-2"><Layers className="w-4 h-4 text-indigo-500" />Cards by subject</h3>
          <div className="space-y-2">
            {stats.by_subject.map((b, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg border border-border" data-testid={`fc-subject-row-${i}`}>
                <p className="flex-1 text-sm font-semibold">{b.subject}</p>
                <Badge variant="outline" className="text-[10px]">{b.count} cards</Badge>
                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px]">{b.mastered} mastered</Badge>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" className="mt-3" onClick={seed} disabled={generating} data-testid="flashcards-add-more">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Pull new cards from study material
          </Button>
        </div>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, color, label, value, testId }) {
  const cls = {
    indigo: 'text-indigo-500 bg-indigo-100 dark:bg-indigo-950/40',
    amber:  'text-amber-500 bg-amber-100 dark:bg-amber-950/40',
    rose:   'text-rose-500 bg-rose-100 dark:bg-rose-950/40',
    emerald:'text-emerald-500 bg-emerald-100 dark:bg-emerald-950/40',
  }[color];
  return (
    <div className="p-4 rounded-2xl border border-border bg-card" data-testid={testId}>
      <div className={`w-8 h-8 rounded-lg ${cls} flex items-center justify-center mb-1.5`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}
